import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { type ReactElement, useId } from 'react';
import { useTranslation } from 'react-i18next';

import { SectionHeader } from '../../components/SectionHeader';
import { StatusChip } from '../../components/StatusChip';
import { reasonMessage } from '../../i18n';
import type { ApiError } from '../../lib/api/api-client';

import { ReasonAlert } from './ReasonAlert';
import type { TelegramState } from './account-model';

/** What the Telegram section shows: the `GET /me` read, then the state of S8 R19. */
export type TelegramView =
  | { readonly kind: 'loading' }
  | { readonly kind: 'failed'; readonly error: ApiError }
  | { readonly kind: TelegramState };

/** Props of {@link TelegramSection}. */
export interface TelegramSectionProps {
  readonly view: TelegramView;
  /** Opens the logout sheet; nothing is sent yet (S8 R2). */
  readonly onLogout: () => void;
  /** Opens E2 (S8 R6). */
  readonly onSignIn: () => void;
  /** Reads `GET /me` again (S8 R21). */
  readonly onRetry: () => void;
}

/**
 * The Telegram section of E11 (S8 R1, R6, R19, R21): a state chip, its text, and the action that
 * fits. Never a phone, a session or a code (A-D6, I9).
 */
export function TelegramSection({
  view,
  onLogout,
  onSignIn,
  onRetry,
}: TelegramSectionProps): ReactElement {
  const { t } = useTranslation();
  const headingId = useId();
  const lost = reasonMessage('TELEGRAM_SESSION_LOST');
  return (
    <Box component="section" aria-labelledby={headingId}>
      <SectionHeader id={headingId} title={t('account.telegram.heading')} />
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
        <Stack spacing={1.5} sx={{ alignItems: 'flex-start' }}>
          {view.kind === 'loading' && <Typography>{t('account.loading')}</Typography>}
          {view.kind === 'failed' && (
            <>
              <ReasonAlert error={view.error} />
              <Button variant="outlined" onClick={onRetry}>
                {t('account.retry')}
              </Button>
            </>
          )}
          {view.kind === 'connected' && (
            <>
              <StatusChip tone="success">{t('account.telegram.connected')}</StatusChip>
              <Button variant="outlined" color="error" onClick={onLogout}>
                {t('account.telegram.logout')}
              </Button>
            </>
          )}
          {view.kind === 'disconnected' && (
            <>
              <StatusChip tone="neutral">{t('account.telegram.disconnected')}</StatusChip>
              <Typography>{t('account.telegram.disconnectedText')}</Typography>
              <Button variant="contained" onClick={onSignIn}>
                {t('account.telegram.signIn')}
              </Button>
            </>
          )}
          {view.kind === 'lost' && (
            <>
              <StatusChip tone="danger">{t('account.telegram.lost')}</StatusChip>
              <Typography>{t(lost.key, lost.values)}</Typography>
              <Button variant="contained" onClick={onSignIn}>
                {t('account.telegram.signIn')}
              </Button>
            </>
          )}
          {view.kind === 'unknown' && (
            <>
              <Typography>{t('account.telegram.unknownText')}</Typography>
              <Button variant="outlined" color="error" onClick={onLogout}>
                {t('account.telegram.logout')}
              </Button>
            </>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
